import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  DEFAULT_DEPARTMENT_SLUG,
  useDepartmentSlug,
} from '../../../../../utils/departmentRouting';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

/** NestJS often returns `{ message: string | string[] }` on 4xx/5xx. */
function nestHttpMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const m = (body as { message?: unknown }).message;
  if (typeof m === 'string' && m.trim()) return m.trim();
  if (Array.isArray(m)) {
    const parts = m.filter((x): x is string => typeof x === 'string');
    if (parts.length) return parts.join(' ');
  }
  return undefined;
}

function categoryConflictFields(body: unknown): ('name' | 'slug')[] {
  if (!body || typeof body !== 'object') return [];
  const raw = (body as { fields?: unknown }).fields;
  if (!Array.isArray(raw)) return [];
  const out: ('name' | 'slug')[] = [];
  for (const x of raw) {
    if (x === 'name' || x === 'slug') out.push(x);
  }
  return out;
}

type CategoryRow = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  imageUrl?: string | null;
  sortOrder: number;
  isActive: boolean;
  productCount?: number;
  departmentId?: string;
};

const TRANSLATION_LOCALE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'fr', label: 'French (fr)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'de', label: 'German (de)' },
];

function CategoriesPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const departmentSlug = useDepartmentSlug() ?? DEFAULT_DEPARTMENT_SLUG;
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [workspaceDepartments, setWorkspaceDepartments] = useState<
    Array<{ id: string; slug: string; name: string }>
  >([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  /** Load / list actions only — category form uses `formErrorSummary` + `formFieldErrors`. */
  const [listError, setListError] = useState<string | null>(null);
  const [formErrorSummary, setFormErrorSummary] = useState<string | null>(null);
  const [formFieldErrors, setFormFieldErrors] = useState<{ name?: string; slug?: string }>({});
  const categoryFormRef = React.useRef<HTMLFormElement>(null);

  const [editing, setEditing] = useState<CategoryRow | null>(null);
  /** Create form is hidden until "+ Add category" is used; edit mode always opens the panel. */
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    imageUrl: '',
    sortOrder: '0',
  });
  const [saving, setSaving] = useState(false);
  const [translationLocale, setTranslationLocale] = useState('fr');
  const [translationForm, setTranslationForm] = useState({
    name: '',
    description: '',
    imageAlt: '',
  });
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translationSaving, setTranslationSaving] = useState(false);
  const [translationSuccess, setTranslationSuccess] = useState<string | null>(null);

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/departments/workspace`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ id?: string; slug?: string; name?: string }>;
        if (!cancelled) {
          setWorkspaceDepartments(
            (Array.isArray(rows) ? rows : [])
              .map((row) => ({
                id: String(row.id || '').trim(),
                slug: String(row.slug || '').trim().toLowerCase(),
                name: String(row.name || '').trim(),
              }))
              .filter((row) => Boolean(row.id && row.slug)),
          );
        }
      } catch {
        if (!cancelled) setWorkspaceDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const scope = String(workspaceScope || '').trim().toLowerCase();
      const isAllScope = !scope || scope === '__all__';
      const url = new URL(`${API_BASE}/catalog/categories/workspace`);
      if (!isAllScope) {
        url.searchParams.set('departmentSlug', departmentSlug);
      }
      const res = await authorizedFetch(url.toString());
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          nestHttpMessage(body)
            ?? t('backoffice.categories_load_error', 'Unable to load categories.'),
        );
      }
      const data = (await res.json()) as CategoryRow[];
      setCategories(data);
    } catch (err) {
      setListError(err instanceof Error ? err.message : t('backoffice.categories_load_error', 'Unable to load categories.'));
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, departmentSlug, t, workspaceScope]);

  useEffect(() => {
    const hasFieldErr = Boolean(formFieldErrors.name || formFieldErrors.slug);
    if ((!formErrorSummary && !hasFieldErr) || !categoryFormRef.current) return;
    categoryFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [formErrorSummary, formFieldErrors.name, formFieldErrors.slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = categories.filter((c) => {
    const term = q.trim().toLowerCase();
    if (!term) return true;
    return (
      c.name.toLowerCase().includes(term)
      || c.slug.toLowerCase().includes(term)
      || (c.description || '').toLowerCase().includes(term)
    );
  });

  useEffect(() => {
    setPage(1);
  }, [q]);

  const sorted = filtered
    .slice()
    .sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
    );
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const paged = sorted.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);
  const isAllScope = (() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  })();
  const departmentsById = React.useMemo(
    () =>
      new Map(
        workspaceDepartments.map((dep) => [
          dep.id,
          { name: dep.name || dep.slug, slug: dep.slug },
        ]),
      ),
    [workspaceDepartments],
  );
  const groupedByDepartment = React.useMemo(() => {
    const groups = new Map<
      string,
      {
        departmentId: string;
        departmentName: string;
        departmentSlug: string;
        categories: CategoryRow[];
      }
    >();
    sorted.forEach((cat) => {
      const depId = String(cat.departmentId || '__unknown__');
      const dep = departmentsById.get(depId);
      const departmentName = dep?.name || 'Unknown department';
      const departmentSlug = dep?.slug || 'unknown';
      const existing = groups.get(depId);
      if (existing) {
        existing.categories.push(cat);
      } else {
        groups.set(depId, {
          departmentId: depId,
          departmentName,
          departmentSlug,
          categories: [cat],
        });
      }
    });
    return Array.from(groups.values()).sort((a, b) =>
      a.departmentName.localeCompare(b.departmentName),
    );
  }, [departmentsById, sorted]);
  const switchWorkspaceScope = React.useCallback((nextDepartmentSlug: string) => {
    const slug = String(nextDepartmentSlug || '').trim().toLowerCase();
    if (!slug || typeof window === 'undefined') return;
    window.localStorage.setItem('workspace_department_scope', slug);
    window.dispatchEvent(
      new CustomEvent('workspace-department-scope:change', {
        detail: { scope: slug },
      }),
    );
  }, []);

  const formPanelVisible = createFormOpen || editing !== null;

  const openCreateForm = () => {
    setFormFieldErrors({});
    setFormErrorSummary(null);
    setEditing(null);
    setForm({
      name: '',
      slug: '',
      description: '',
      imageUrl: '',
      sortOrder: '0',
    });
    setCreateFormOpen(true);
  };

  const closeFormPanel = () => {
    setFormFieldErrors({});
    setFormErrorSummary(null);
    setEditing(null);
    setCreateFormOpen(false);
    setForm({
      name: '',
      slug: '',
      description: '',
      imageUrl: '',
      sortOrder: '0',
    });
    setTranslationSuccess(null);
    setTranslationForm({ name: '', description: '', imageAlt: '' });
  };

  const startEdit = (row: CategoryRow) => {
    setFormFieldErrors({});
    setFormErrorSummary(null);
    setEditing(row);
    setForm({
      name: row.name,
      slug: row.slug,
      description: row.description || '',
      imageUrl: row.imageUrl || '',
      sortOrder: String(row.sortOrder ?? 0),
    });
    setTranslationSuccess(null);
    setTranslationForm({ name: '', description: '', imageAlt: '' });
  };

  useEffect(() => {
    let cancelled = false;
    if (!editing?.id) return () => { cancelled = true; };
    (async () => {
      setTranslationLoading(true);
      try {
        const url = new URL(`${API_BASE}/catalog/categories/${editing.id}/translations`);
        url.searchParams.set('locale', translationLocale);
        const res = await authorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          translations: { name?: string; description?: string; imageAlt?: string };
        };
        if (!cancelled) {
          setTranslationForm({
            name: data.translations.name || '',
            description: data.translations.description || '',
            imageAlt: data.translations.imageAlt || '',
          });
        }
      } finally {
        if (!cancelled) setTranslationLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, editing?.id, translationLocale]);

  const saveTranslations = async () => {
    if (!editing?.id) return;
    setTranslationSaving(true);
    setTranslationSuccess(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/catalog/categories/${editing.id}/translations`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locale: translationLocale,
            name: translationForm.name,
            description: translationForm.description,
            imageAlt: translationForm.imageAlt,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          nestHttpMessage(body) || 'Unable to save category translations.',
        );
      }
      setTranslationSuccess('Translation saved successfully.');
    } catch (err) {
      setFormErrorSummary(
        err instanceof Error ? err.message : 'Unable to save category translations.',
      );
    } finally {
      setTranslationSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormErrorSummary(null);
    setFormFieldErrors({});
    try {
      const trimmedImg = form.imageUrl.trim();
      const trimmedSlug = form.slug.trim();
      const payload: {
        name: string;
        slug?: string;
        description?: string;
        imageUrl?: string | null;
        sortOrder: number;
        departmentSlug: string;
      } = {
        name: form.name,
        description: form.description || undefined,
        sortOrder: Number(form.sortOrder || 0),
        departmentSlug,
      };
      if (trimmedSlug) {
        payload.slug = trimmedSlug;
      }
      if (editing) {
        payload.imageUrl = trimmedImg === '' ? null : trimmedImg;
      } else if (trimmedImg) {
        payload.imageUrl = trimmedImg;
      }
      const url = editing
        ? `${API_BASE}/catalog/categories/${editing.id}`
        : `${API_BASE}/catalog/categories`;
      const method = editing ? 'PATCH' : 'POST';
      const res = await authorizedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const fromApi = nestHttpMessage(body);

        if (res.status === 409) {
          const fields = categoryConflictFields(body);
          const nextFieldErr: { name?: string; slug?: string } = {};
          if (fields.includes('name')) {
            nextFieldErr.name = t(
              'backoffice.categories_field_name_taken',
              'This name is already used by another category.',
            );
          }
          if (fields.includes('slug')) {
            nextFieldErr.slug = t(
              'backoffice.categories_field_slug_taken',
              'This URL slug is already used by another category.',
            );
          }
          setFormFieldErrors(nextFieldErr);
          const hasFieldErr = Object.keys(nextFieldErr).length > 0;
          setFormErrorSummary(
            hasFieldErr
              ? null
              : (fromApi
                ?? t(
                  'backoffice.categories_duplicate_generic',
                  'A category with this name or URL slug already exists.',
                )),
          );
          return;
        }

        setFormFieldErrors({});
        setFormErrorSummary(
          fromApi
            ?? t('backoffice.categories_save_error', 'Unable to save category.'),
        );
        return;
      }
      await load();
      closeFormPanel();
    } catch (err) {
      setFormFieldErrors({});
      setFormErrorSummary(
        err instanceof Error ? err.message : t('backoffice.categories_save_error', 'Unable to save category.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: CategoryRow) => {
    setListError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/catalog/categories/${row.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !row.isActive }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message =
          nestHttpMessage(body)
          ?? t('backoffice.categories_save_error', 'Unable to save category.');
        throw new Error(message);
      }
      await load();
    } catch (err) {
      setListError(err instanceof Error ? err.message : t('backoffice.categories_save_error', 'Unable to save category.'));
    }
  };

  const remove = async (row: CategoryRow) => {
    const ok = window.confirm(t('backoffice.categories_confirm_delete', 'Delete this category?'));
    if (!ok) return;
    setListError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/catalog/categories/${row.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message =
          nestHttpMessage(body)
          ?? t('backoffice.categories_delete_error', 'Unable to delete category.');
        throw new Error(message);
      }
      await load();
    } catch (err) {
      setListError(err instanceof Error ? err.message : t('backoffice.categories_delete_error', 'Unable to delete category.'));
    }
  };

  const nameInputClass = [
    'w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 outline-none',
    formFieldErrors.name
      ? 'border-rose-500 focus:border-rose-600 focus:ring-2 focus:ring-rose-300/80'
      : 'border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70',
  ].join(' ');

  const slugInputClass = [
    'w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 outline-none',
    formFieldErrors.slug
      ? 'border-rose-500 focus:border-rose-600 focus:ring-2 focus:ring-rose-300/80'
      : 'border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70',
  ].join(' ');

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <div className="mb-1 flex items-start justify-between gap-3">
            <h1 className="workspace-page-title">
              {t('backoffice.categories_title', 'Categories')}
            </h1>
          </div>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.categories_subtitle', 'Manage product categories for the store and landing page.')}
          </p>
        </section>

        <section className="workspace-panel">
          {listError && (
            <p className="mb-3 text-sm text-red-600" role="alert">
              {listError}
            </p>
          )}

          <div className="mb-6 grid gap-3 sm:grid-cols-3">
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('backoffice.categories_count', 'categories')}
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                {categories.length}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {categories.filter((c) => c.isActive).length} active •{' '}
                {categories.filter((c) => !c.isActive).length} inactive
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:col-span-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Quick search
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, slug, description"
                  className="w-full flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 sm:min-w-[320px]"
                />
                <button
                  type="button"
                  onClick={() => setQ('')}
                  disabled={!q}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('backoffice.hero_btn_clear', 'Clear')}
                </button>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Showing {filtered.length} / {categories.length}
              </div>
            </div>
          </div>

          <div className={['grid gap-6', formPanelVisible ? 'lg:grid-cols-2' : ''].join(' ').trim()}>
            {formPanelVisible ? (
            <form
              ref={categoryFormRef}
              onSubmit={handleSubmit}
              className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {editing
                      ? t('backoffice.categories_update', 'Update')
                      : t('backoffice.categories_add', 'Add category')}
                  </p>
                  <p className="mt-1 text-lg font-semibold tracking-tight text-slate-900">
                    {editing ? editing.name : 'New category'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Use categories to group products and improve navigation.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeFormPanel}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:border-slate-400"
                >
                  {t('backoffice.hero_btn_clear', 'Clear')}
                </button>
              </div>

              {formErrorSummary ? (
                <div
                  className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900"
                  role="alert"
                >
                  {formErrorSummary}
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label
                    className={[
                      'block text-xs font-medium',
                      formFieldErrors.name ? 'text-rose-800' : 'text-slate-700',
                    ].join(' ')}
                    htmlFor="catName"
                  >
                    {t('backoffice.categories_name', 'Name')}
                  </label>
                  <input
                    id="catName"
                    value={form.name}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, name: e.target.value }));
                      setFormFieldErrors((f) => ({ ...f, name: undefined }));
                      setFormErrorSummary(null);
                    }}
                    className={nameInputClass}
                    aria-invalid={Boolean(formFieldErrors.name)}
                    aria-describedby={formFieldErrors.name ? 'catName-error' : undefined}
                    required
                  />
                  {formFieldErrors.name ? (
                    <p id="catName-error" className="text-xs font-medium text-rose-600">
                      {formFieldErrors.name}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <label
                    className={[
                      'block text-xs font-medium',
                      formFieldErrors.slug ? 'text-rose-800' : 'text-slate-700',
                    ].join(' ')}
                    htmlFor="catSlug"
                  >
                    {t('backoffice.categories_slug', 'Slug (URL)')}
                    <span className="font-normal text-slate-500">
                      {' '}
                      ({t('common.optional', 'optional')})
                    </span>
                  </label>
                  <input
                    id="catSlug"
                    value={form.slug}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, slug: e.target.value }));
                      setFormFieldErrors((f) => ({ ...f, slug: undefined }));
                      setFormErrorSummary(null);
                    }}
                    placeholder={t('backoffice.categories_slug_placeholder', 'e.g. vegetables')}
                    className={slugInputClass}
                    aria-invalid={Boolean(formFieldErrors.slug)}
                    aria-describedby="catSlug-hint"
                  />
                  {formFieldErrors.slug ? (
                    <p id="catSlug-error" className="text-xs font-medium text-rose-600">
                      {formFieldErrors.slug}
                    </p>
                  ) : null}
                </div>
                <p id="catSlug-hint" className="text-[11px] text-slate-500 sm:col-span-2">
                  {t(
                    'backoffice.categories_unique_hint',
                    'Category names must be unique. Leave slug blank to generate one from the name.',
                  )}
                </p>
                <div className="space-y-1 sm:col-span-2">
                  <label className="block text-xs font-medium text-slate-700" htmlFor="catDesc">
                    {t('backoffice.categories_description', 'Description')}
                  </label>
                  <textarea
                    id="catDesc"
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  />
                  <p className="text-[11px] text-slate-500">
                    Optional. Shown on the landing page if enabled.
                  </p>
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <label className="block text-xs font-medium text-slate-700" htmlFor="catImageUrl">
                    {t('backoffice.categories_image_url', 'Storefront strip image URL')}
                  </label>
                  <input
                    id="catImageUrl"
                    type="url"
                    value={form.imageUrl}
                    onChange={(e) => setForm((p) => ({ ...p, imageUrl: e.target.value }))}
                    placeholder="https://… or /uploads/…"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  />
                  <p className="text-[11px] text-slate-500">
                    Optional. Shown on the home page category row. Leave empty to use an automatic photo.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-4">
                    <div className="space-y-1 sm:w-40 sm:flex-none">
                      <label className="block text-xs font-medium text-slate-700" htmlFor="catOrder">
                        {t('backoffice.categories_sort_order', 'Sort order')}
                      </label>
                      <input
                        id="catOrder"
                        type="number"
                        value={form.sortOrder}
                        onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="submit"
                        disabled={saving}
                        className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                      >
                        {saving
                          ? t('backoffice.categories_saving', 'Saving…')
                          : editing
                            ? t('backoffice.categories_update', 'Update')
                            : t('backoffice.categories_create', 'Create')}
                      </button>
                      {!editing && (
                        <button
                          type="button"
                          onClick={closeFormPanel}
                          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:border-slate-400"
                        >
                          {t('backoffice.hero_btn_clear', 'Clear')}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">Lower comes first.</p>
                </div>
                {editing ? (
                  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:col-span-2">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Dynamic content translations
                      </p>
                      <select
                        value={translationLocale}
                        onChange={(e) => {
                          setTranslationLocale(e.target.value);
                          setTranslationSuccess(null);
                        }}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                      >
                        {TRANSLATION_LOCALE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {translationLoading ? (
                      <p className="text-xs text-slate-500">Loading translations...</p>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="text-xs font-medium text-slate-700">
                          Localized name
                          <input
                            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={translationForm.name}
                            onChange={(e) => {
                              setTranslationForm((p) => ({ ...p, name: e.target.value }));
                              setTranslationSuccess(null);
                            }}
                          />
                        </label>
                        <label className="text-xs font-medium text-slate-700">
                          Localized image alt
                          <input
                            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={translationForm.imageAlt}
                            onChange={(e) => {
                              setTranslationForm((p) => ({ ...p, imageAlt: e.target.value }));
                              setTranslationSuccess(null);
                            }}
                          />
                        </label>
                        <label className="text-xs font-medium text-slate-700 sm:col-span-2">
                          Localized description
                          <textarea
                            rows={3}
                            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={translationForm.description}
                            onChange={(e) => {
                              setTranslationForm((p) => ({ ...p, description: e.target.value }));
                              setTranslationSuccess(null);
                            }}
                          />
                        </label>
                        <div className="sm:col-span-2">
                          <button
                            type="button"
                            onClick={saveTranslations}
                            disabled={translationSaving}
                            className="rounded-md border border-brand-500 bg-white px-4 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-60"
                          >
                            {translationSaving ? 'Saving translation...' : 'Save translation'}
                          </button>
                          {translationSuccess ? (
                            <p className="mt-2 text-xs font-medium text-emerald-700">
                              {translationSuccess}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </form>
            ) : null}

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('backoffice.categories_list', 'All categories')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (isAllScope) return;
                    openCreateForm();
                    setQ('');
                    setPage(1);
                  }}
                  disabled={isAllScope}
                  title={
                    isAllScope
                      ? 'Select a specific department scope to create a category.'
                      : undefined
                  }
                  className="rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  + {t('backoffice.categories_add', 'Add category')}
                </button>
              </div>
              {isAllScope ? (
                <p className="mb-3 text-xs font-medium text-amber-700">
                  Select a specific department scope to add a category.
                </p>
              ) : null}

              {loading ? (
                <PaginatedListLoading
                  compact
                  minHeightClass="min-h-[200px]"
                  title={t('backoffice.categories_loading', 'Loading…')}
                  skeletonRows={4}
                />
              ) : filtered.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
                  <p className="text-sm font-semibold text-slate-900">
                    {categories.length === 0
                      ? t('backoffice.categories_empty', 'No categories yet.')
                      : 'No matches.'}
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    {categories.length === 0
                      ? 'Create your first category to organize products.'
                      : 'Try a different search term.'}
                  </p>
                </div>
              ) : (
                <>
                  {!isAllScope ? (
                    <div className="space-y-2">
                      {paged.map((c) => {
                      const isEditing = editing?.id === c.id;
                      return (
                        <div
                          key={c.id}
                          className={[
                            'rounded-xl border bg-white p-4 transition',
                            isEditing ? 'border-brand-300 ring-2 ring-brand-200/60' : 'border-slate-200 hover:border-slate-300',
                          ].join(' ')}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="truncate text-sm font-semibold text-slate-900">{c.name}</p>
                                {c.isActive ? (
                                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                                    {t('backoffice.categories_active', 'Active')}
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                                    {t('backoffice.suppliers_status_inactive', 'Inactive')}
                                  </span>
                                )}
                                <span className="rounded-full bg-slate-900/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                                  Sort {c.sortOrder ?? 0}
                                </span>
                              </div>
                              <p className="mt-1 truncate text-xs text-slate-500">{c.slug}</p>
                              {c.description ? (
                                <p className="mt-2 line-clamp-2 text-xs text-slate-600">{c.description}</p>
                              ) : null}
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleActive(c)}
                                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-700 shadow-sm hover:border-slate-400"
                              >
                                {c.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                type="button"
                                onClick={() => startEdit(c)}
                                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-700 shadow-sm hover:border-slate-400"
                              >
                                {t('backoffice.hero_btn_edit', 'Edit')}
                              </button>
                              <button
                                type="button"
                                onClick={() => remove(c)}
                                disabled={(c.productCount ?? 0) > 0}
                                title={
                                  (c.productCount ?? 0) > 0
                                    ? 'Cannot delete: category is used by products.'
                                    : undefined
                                }
                                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-rose-700 shadow-sm hover:bg-rose-100"
                              >
                                {t('backoffice.hero_btn_delete', 'Delete')}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {groupedByDepartment.map((group) => (
                        <div
                          key={group.departmentId}
                          className="workspace-stat-card"
                        >
                          <div className="mb-3 flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-slate-900/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                              {group.departmentName}
                            </span>
                            <span className="text-[11px] text-slate-500">/{group.departmentSlug}</span>
                            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                              {group.categories.length} categor{group.categories.length > 1 ? 'ies' : 'y'}
                            </span>
                          </div>
                          <div className="space-y-1.5">
                            {group.categories.map((cat) => (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => switchWorkspaceScope(group.departmentSlug)}
                                className="w-full rounded-lg border border-slate-100 px-3 py-2 text-left text-xs text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/40"
                              >
                                <span className="font-semibold text-slate-900">{cat.name}</span>
                                <span className="ml-2 text-slate-500">/{cat.slug}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {totalPages > 1 && !isAllScope && (
                    <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                      <span className="font-semibold">
                        Page {pageSafe} / {totalPages}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={pageSafe <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Prev
                        </button>
                        <button
                          type="button"
                          disabled={pageSafe >= totalPages}
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>
    </MainLayout>
  );
}

export default CategoriesPage;

